import { useEffect, useState } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { MapPin, Download, MessageSquare, Briefcase, GraduationCap, Award, Pencil, Images, Star, PlayCircle } from "lucide-react";
import { Avatar, Badge, Rating, Verified, Feature } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { useListing } from "../lib/useListing";
import { setMeta, resetMeta } from "../lib/seo";
import { people as mockPeople } from "../data/mock";
import { SaveButton } from "../components/SaveButton";
import { MobileActionBar } from "../components/MobileActionBar";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { messagesHref, openResumePrint } from "../lib/actions";

export default function ResumeDetail() {
  const [, params] = useRoute("/p/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";
  const { item: p, state } = useListing("people", id, mockPeople);
  const [media, setMedia] = useState<any[]>([]);

  useEffect(() => {
    if (p) setMeta({ title: `${p.title || p.name} — KontrataPH`, description: [p.name, p.city, p.years ? `${p.years} yrs experience` : ""].filter(Boolean).join(" · ") || undefined });
    return () => resetMeta();
  }, [p]);

  // Portfolio media is keyed by the résumé owner's user id (public endpoint).
  useEffect(() => {
    let alive = true;
    if (p?.ownerUserId) api.workerMedia(p.ownerUserId).then((m) => { if (alive) setMedia(Array.isArray(m) ? m : []); }).catch(() => { if (alive) setMedia([]); });
    else setMedia([]);
    return () => { alive = false; };
  }, [p?.ownerUserId]);

  if (state === "loading") return <DetailLoading label="Loading profile…" />;
  if (state === "notfound" || !p) return <DetailNotFound title="Profile not found" backHref="/marketplace?tab=People" backLabel="Browse People" />;

  const skills: string[] = Array.isArray(p.skills) ? p.skills : [];
  const experience: any[] = Array.isArray((p as any).experience) ? (p as any).experience : [];
  const education: any[] = Array.isArray((p as any).education) ? (p as any).education : [];

  // CTA logic shared by the header buttons and the mobile sticky bar
  const isSelf = !!user && p.ownerUserId === user.id;
  const contactHref = messagesHref(p.ownerUserId, { type: "resume", id, name: p.name });
  const hireHref = messagesHref(p.ownerUserId, { type: "resume", id, name: p.name, intent: "hire" });
  const disabledTitle = isSelf ? "This is your listing" : "Contact isn't available on this sample profile";
  const go = (target: string | null) => () => { if (!user) return navigate("/login"); if (target) navigate(target); };
  const ctaDisabled = !!user && (isSelf || !contactHref);

  const aboutLine = [p.title, p.city ? `based in ${p.city}` : "", p.years ? `${p.years} year${p.years === 1 ? "" : "s"} of experience` : ""].filter(Boolean).join(" · ")
    + (skills.length ? `. Skilled in ${skills.slice(0, 6).join(", ")}.` : ".");

  return (
    <div className="container-x py-6 pb-24 lg:pb-6">
      <div className="text-sm text-mut mb-4"><Link href="/marketplace?tab=People" className="hover:text-navy">People &amp; Resumes</Link> / {p.name}</div>
      <div className="card p-6 flex flex-col sm:flex-row gap-5 items-start">
        <Avatar initials={p.initials} size={88} />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap"><h1 className="text-2xl font-extrabold text-navy">{p.name}</h1>{p.verified && <Verified label="Verified ID" />}{(p as any).isStar && <span className="chip bg-brand-gold/15 text-[#8a5a00] font-bold"><Star className="w-3.5 h-3.5 fill-brand-gold text-brand-gold" />Star</span>}</div>
          <p className="text-mut">{p.title}</p>
          <p className="text-sm text-mut flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" />{p.city}, Philippines</p>
          <div className="flex items-center gap-4 mt-2"><Rating value={p.rating} count={p.reviews} /><span className="text-sm text-mut">{p.years} yrs experience</span>{p.available && <span className="text-sm font-semibold text-brand-green">● Available Now</span>}</div>
          <div className="flex flex-wrap gap-2 mt-4">
            {isSelf ? (
              <>
                <Link href="/dashboard?view=people" className="btn-navy"><Pencil className="w-4 h-4" />Manage Résumé</Link>
                <button onClick={() => openResumePrint(p)} className="btn-outline"><Download className="w-4 h-4" />Download Résumé</button>
              </>
            ) : (
              <>
                <button onClick={go(contactHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledTitle : undefined}
                  className="btn-navy disabled:opacity-50 disabled:cursor-not-allowed"><MessageSquare className="w-4 h-4" />Contact</button>
                <button onClick={go(hireHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledTitle : undefined}
                  className="btn-red disabled:opacity-50 disabled:cursor-not-allowed">Hire</button>
                {user
                  ? <SaveButton kind="person" refId={id} label="Save" />
                  : <button onClick={() => navigate("/login")} className="btn-outline"><Download className="w-4 h-4" />Save</button>}
                <button onClick={() => openResumePrint(p)} className="btn-outline"><Download className="w-4 h-4" />Download Résumé</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5"><h3 className="font-bold text-navy mb-2">About</h3><p className="text-sm text-mut">{aboutLine}</p></div>
          {(p as any).videoIntroUrl && (<div className="card p-5"><h3 className="font-bold text-navy mb-3 flex items-center gap-2"><PlayCircle className="w-4 h-4" />Video introduction</h3>{ytEmbed((p as any).videoIntroUrl) ? <div className="aspect-video rounded-lg overflow-hidden bg-navy-50"><iframe src={ytEmbed((p as any).videoIntroUrl) as string} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Video introduction" /></div> : <a href={(p as any).videoIntroUrl} target="_blank" rel="noreferrer" className="btn-navy"><PlayCircle className="w-4 h-4" />Watch video introduction</a>}</div>)}
          {experience.length > 0 && (
            <div className="card p-5"><h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Briefcase className="w-4 h-4" />Experience</h3>
              <div className="space-y-3 text-sm">
                {experience.map((x, i) => (
                  <div key={i}><div className="font-semibold text-navy">{x.title || x.role}</div><div className="text-mut">{[x.company, x.period].filter(Boolean).join(" · ")}</div></div>
                ))}
              </div>
            </div>
          )}
          {media.length > 0 && (
            <div className="card p-5"><h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Images className="w-4 h-4" />Portfolio</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {media.map((m) => (
                  <a key={m.id} href={m.url} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden bg-navy-50 border border-line">
                    <img src={m.url} alt={m.title || "Portfolio item"} className="w-full h-full object-cover" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="card p-5"><h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Award className="w-4 h-4" />Skills</h3><div className="flex flex-wrap gap-2">{skills.length ? skills.map((s) => <Badge key={s}>{s}</Badge>) : <span className="text-sm text-mut">No skills listed yet.</span>}</div></div>
          {education.length > 0 && (
            <div className="card p-5"><h3 className="font-bold text-navy mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4" />Education</h3>
              <div className="space-y-2 text-sm">
                {education.map((e, i) => (
                  <p key={i}><span className="font-semibold text-navy">{e.degree || e.title}</span>{e.school ? <><br /><span className="text-mut">{[e.school, e.period].filter(Boolean).join(" · ")}</span></> : null}</p>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="space-y-4">
          <div className="card p-5"><h3 className="font-bold text-navy mb-3">Service Offerings</h3>
            {skills.length ? <ul className="space-y-2">{skills.map((s) => <Feature key={s}>{s}</Feature>)}</ul> : <p className="text-sm text-mut">No offerings listed yet.</p>}
          </div>
          <div className="card p-5"><h3 className="font-bold text-navy mb-3">Details</h3>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-mut">Rate</dt><dd className="font-semibold text-navy">{p.rate || "On request"}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Availability</dt><dd className="font-semibold text-navy">{p.available ? "Available now" : "Currently busy"}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Response</dt><dd className="font-semibold text-navy">Within hours</dd></div>
            </dl>
          </div>
        </div>
      </div>

      {isSelf ? (
        <MobileActionBar>
          <Link href="/dashboard?view=people" className="btn-navy flex-1"><Pencil className="w-4 h-4" />Manage Résumé</Link>
        </MobileActionBar>
      ) : (
        <MobileActionBar>
          <button onClick={go(contactHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledTitle : undefined}
            className="btn-navy flex-1 disabled:opacity-50 disabled:cursor-not-allowed"><MessageSquare className="w-4 h-4" />Contact</button>
          <button onClick={go(hireHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledTitle : undefined}
            className="btn-red flex-1 disabled:opacity-50 disabled:cursor-not-allowed">Hire</button>
        </MobileActionBar>
      )}
    </div>
  );
}


function ytEmbed(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    if (u.hostname === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video/${u.pathname.split("/").filter(Boolean)[0]}`;
  } catch { /* not embeddable */ }
  return null;
}
